/**
 * AI Tasks Dashboard Page
 * Shows all AI tasks with filtering and status
 */

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Badge,
  Progress,
} from "@learn-me/ui";
import { adminApi, type AITask } from "@/features/admin/api/adminApi";
import {
  Sparkles,
  Image,
  Search,
  Trash2,
  BarChart3,
  Loader2,
  RefreshCw,
  X,
  Clock,
  CheckCircle,
  XCircle,
  Pause,
  ArrowRight,
  Filter,
} from "lucide-react";
import toast from "react-hot-toast";

type FilterStatus = "all" | "active" | "completed" | "failed";

const TASK_TYPE_LABELS: Record<string, string> = {
  GENERATE_DIAGRAMS: "Generar diagramas",
  VALIDATE_CONTENT: "Validar contenido",
  CLEAN_CONTENT: "Limpiar contenido",
  ANALYZE_QUALITY: "Analizar calidad",
  REGENERATE_SECTION: "Regenerar sección",
  REGENERATE_CHAPTER: "Regenerar capítulo",
  REGENERATE_QUIZ: "Regenerar quiz",
  ENHANCE_QUIZ: "Mejorar quiz",
  GENERATE_AUDIO: "Generar audio",
};

const TASK_TYPE_ICONS: Record<string, React.ReactNode> = {
  GENERATE_DIAGRAMS: <Image className="h-4 w-4" />,
  VALIDATE_CONTENT: <Search className="h-4 w-4" />,
  CLEAN_CONTENT: <Trash2 className="h-4 w-4" />,
  ANALYZE_QUALITY: <BarChart3 className="h-4 w-4" />,
};

const STATUS_COLORS: Record<string, string> = {
  PENDING: "bg-yellow-100 text-yellow-800 border-yellow-200",
  PROCESSING: "bg-blue-100 text-blue-800 border-blue-200",
  COMPLETED: "bg-green-100 text-green-800 border-green-200",
  FAILED: "bg-red-100 text-red-800 border-red-200",
  CANCELLED: "bg-gray-100 text-gray-800 border-gray-200",
};

const STATUS_LABELS: Record<string, string> = {
  PENDING: "Pendiente",
  PROCESSING: "En proceso",
  COMPLETED: "Completado",
  FAILED: "Fallido",
  CANCELLED: "Cancelado",
};

const STATUS_ICONS: Record<string, React.ReactNode> = {
  PENDING: <Clock className="h-4 w-4" />,
  PROCESSING: <Loader2 className="h-4 w-4 animate-spin" />,
  COMPLETED: <CheckCircle className="h-4 w-4" />,
  FAILED: <XCircle className="h-4 w-4" />,
  CANCELLED: <Pause className="h-4 w-4" />,
};

export default function AITasksPage() {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<FilterStatus>("all");

  // Fetch active tasks
  const { data: activeTasks, isLoading: loadingActive } = useQuery({
    queryKey: ["ai-tasks-active"],
    queryFn: () => adminApi.getActiveTasks(50),
    refetchInterval: 3000, // Poll every 3 seconds
  });

  // Fetch my tasks history
  const { data: myTasks, isLoading: loadingHistory } = useQuery({
    queryKey: ["ai-tasks-history"],
    queryFn: () => adminApi.getMyTasks(100),
  });

  // Cancel task mutation
  const cancelMutation = useMutation({
    mutationFn: (taskId: string) => adminApi.cancelAITask(taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ai-tasks-active"] });
      queryClient.invalidateQueries({ queryKey: ["ai-tasks-history"] });
      toast.success("Tarea cancelada");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Error al cancelar la tarea");
    },
  });

  // Filter tasks
  const filteredTasks = myTasks?.filter((task) => {
    if (filter === "all") return true;
    if (filter === "active")
      return task.status === "PENDING" || task.status === "PROCESSING";
    if (filter === "completed") return task.status === "COMPLETED";
    if (filter === "failed")
      return task.status === "FAILED" || task.status === "CANCELLED";
    return true;
  });

  const isLoading = loadingActive || loadingHistory;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Sparkles className="h-8 w-8 text-purple-600" />
            Tareas IA
          </h1>
          <p className="text-muted-foreground mt-1">
            Gestiona las tareas de inteligencia artificial
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => {
            queryClient.invalidateQueries({ queryKey: ["ai-tasks-active"] });
            queryClient.invalidateQueries({ queryKey: ["ai-tasks-history"] });
          }}
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Actualizar
        </Button>
      </div>

      {/* Active Tasks */}
      {activeTasks && activeTasks.length > 0 && (
        <Card className="border-blue-200 bg-blue-50/50">
          <CardHeader>
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
              Tareas en progreso ({activeTasks.length})
            </h2>
          </CardHeader>
          <CardContent className="space-y-4">
            {activeTasks.map((task) => (
              <ActiveTaskCard
                key={task.id}
                task={task}
                onCancel={() => cancelMutation.mutate(task.id)}
                isCancelling={cancelMutation.isPending}
              />
            ))}
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex items-center gap-2">
        <Filter className="h-4 w-4 text-muted-foreground" />
        <div className="flex gap-2">
          {(["all", "active", "completed", "failed"] as FilterStatus[]).map(
            (status) => (
              <Button
                key={status}
                variant={filter === status ? "default" : "outline"}
                size="sm"
                onClick={() => setFilter(status)}
              >
                {status === "all" && "Todas"}
                {status === "active" && "Activas"}
                {status === "completed" && "Completadas"}
                {status === "failed" && "Fallidas"}
              </Button>
            ),
          )}
        </div>
      </div>

      {/* Task History */}
      <Card>
        <CardHeader>
          <h2 className="text-lg font-semibold">Historial de tareas</h2>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : filteredTasks && filteredTasks.length > 0 ? (
            <div className="space-y-3">
              {filteredTasks.map((task) => (
                <TaskCard key={task.id} task={task} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Sparkles className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No hay tareas en el historial</p>
              <p className="text-sm mt-1">
                Las tareas IA aparecerán aquí cuando las ejecutes
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ActiveTaskCard({
  task,
  onCancel,
  isCancelling,
}: {
  task: AITask;
  onCancel: () => void;
  isCancelling: boolean;
}) {
  return (
    <div className="p-4 bg-white rounded-lg border border-blue-200 shadow-sm">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-blue-100">
            {TASK_TYPE_ICONS[task.type] || <Sparkles className="h-4 w-4" />}
          </div>
          <div>
            <p className="font-medium">
              {TASK_TYPE_LABELS[task.type] || task.type}
            </p>
            {task.course && (
              <Link
                to={`/admin/courses/${task.course.id}/edit`}
                className="text-sm text-blue-600 hover:underline"
              >
                {task.course.title}
              </Link>
            )}
          </div>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={onCancel}
          disabled={isCancelling}
          className="text-red-600 hover:text-red-700 hover:bg-red-50"
        >
          {isCancelling ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <X className="h-4 w-4" />
          )}
        </Button>
      </div>

      <div className="space-y-2">
        {task.currentStep && (
          <p className="text-sm text-muted-foreground">{task.currentStep}</p>
        )}
        <Progress value={task.progress} className="h-2" />
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>{task.progress}%</span>
          {task.processedItems !== null && task.totalItems !== null && (
            <span>
              {task.processedItems} / {task.totalItems} items
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function TaskCard({ task }: { task: AITask }) {
  const formattedDate = new Date(task.createdAt).toLocaleDateString("es", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <div className="p-4 bg-muted/30 rounded-lg border hover:bg-muted/50 transition-colors">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-muted">
            {TASK_TYPE_ICONS[task.type] || (
              <Sparkles className="h-4 w-4 text-muted-foreground" />
            )}
          </div>
          <div>
            <p className="font-medium">
              {TASK_TYPE_LABELS[task.type] || task.type}
            </p>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>{formattedDate}</span>
              {task.course && (
                <>
                  <span>-</span>
                  <Link
                    to={`/admin/courses/${task.course.id}/edit`}
                    className="hover:text-foreground hover:underline"
                  >
                    {task.course.title}
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Badge
            variant="outline"
            className={`${STATUS_COLORS[task.status]} flex items-center gap-1`}
          >
            {STATUS_ICONS[task.status]}
            {STATUS_LABELS[task.status]}
          </Badge>

          {task.course && (
            <Link to={`/admin/courses/${task.course.id}/edit`}>
              <Button variant="ghost" size="sm">
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* Error message */}
      {task.status === "FAILED" && task.errorMessage && (
        <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
          {task.errorMessage}
        </div>
      )}
    </div>
  );
}
